/** Assign worked time to projects and record absences. */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { DataTable } from "@primereact/ui/datatable";
import { Dialog } from "@primereact/ui/dialog";
import { Select as Dropdown } from "@primereact/ui/select";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { DateInput } from "../../components/ui/controls.js";
import { Inplace } from "@primereact/ui/inplace";
import { Message } from "@primereact/ui/message";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { Tag } from "@primereact/ui/tag";
import { Textarea } from "@primereact/ui/textarea";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "../../lib/api.js";
import { ConfirmButton, ErrorPanel, LoadingPanel } from "../shared.js";
import type { ProjectChoice, TimesheetData } from "./types.js";
import {
  KINDS,
  calendarValue,
  dateOnly,
  duration,
  localTime,
  monday,
  shiftWeek,
  type TimeEntry,
  DayValue,
  Metric,
  TimeMutationMessage,
} from "./time-common.js";

type AssignmentMatrixView = "day" | "week" | "month";
type AssignmentMatrixData = {
  from: string;
  to: string;
  dates: Array<{
    date: string;
    plannedMinutes: number;
    attendanceMinutes: number;
    allocatedMinutes: number;
    absences: Array<{ kind: string; minutes: number }>;
    sheetId: string | null;
    sheetVersion: number | null;
    sheetStatus: string;
  }>;
  allocations: Array<{
    id: string;
    date: string;
    minutes: number;
    version: number;
    project: ProjectChoice;
  }>;
};
function assignmentRange(
  anchor: string,
  view: AssignmentMatrixView,
): { from: string; to: string } {
  const date = calendarValue(anchor) ?? new Date();
  if (view === "day") return { from: dateOnly(date), to: dateOnly(date) };
  if (view === "week") {
    const from = monday(date);
    return { from, to: shiftWeek(from, 6) };
  }
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: dateOnly(from), to: dateOnly(to) };
}
function shiftAssignmentAnchor(
  anchor: string,
  view: AssignmentMatrixView,
  amount: number,
): string {
  const date = calendarValue(anchor) ?? new Date();
  if (view === "month") {
    date.setMonth(date.getMonth() + amount, 1);
    return dateOnly(date);
  }
  date.setDate(date.getDate() + amount * (view === "week" ? 7 : 1));
  return dateOnly(date);
}
function AllocationCell({
  project,
  day,
  value,
}: {
  project: ProjectChoice;
  day: AssignmentMatrixData["dates"][number];
  value: number;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState(value);
  useEffect(() => setMinutes(value), [value]);
  const available = Math.max(
    0,
    day.attendanceMinutes - (day.allocatedMinutes - value),
  );
  const editable = ["draft", "rejected"].includes(day.sheetStatus);
  const save = useMutation({
    mutationFn: () =>
      apiPut("/time/allocations/cell", {
        date: day.date,
        projectId: project.id,
        minutes,
        sheetVersion: day.sheetVersion,
      }),
    onSuccess: async () => {
      setOpen(false);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["time-assignment-matrix"] }),
        client.invalidateQueries({ queryKey: ["time-week"] }),
      ]);
    },
  });
  return (
    <Inplace.Root
      open={open}
      disabled={!editable}
      onOpenChange={(event: { open?: boolean }) => {
        setOpen(Boolean(event.open));
        if (event.open) setMinutes(value);
      }}
    >
      <Inplace.Display
        aria-label={t("time.editAllocationCell", {
          project: project.number,
          date: day.date,
        })}
      >
        <span className="block min-h-10 w-full rounded-md px-2 py-2 text-right font-medium tabular-nums text-app-text hover:bg-[var(--p-content-hover-background)]">
          {value ? duration(value) : "＋"}
        </span>
      </Inplace.Display>
      <Inplace.Content>
        <div className="flex min-w-44 items-center gap-1 p-1">
          <InputNumber.Root
            fluid
            min={0}
            max={available}
            value={Math.min(minutes, available)}
            onValueChange={(event: { value: number | null }) =>
              setMinutes(event.value ?? 0)
            }
          >
            <InputNumber.Input as={Input} autoFocus />
          </InputNumber.Root>
          <Button
            size="small"
            aria-label={t("common.save")}
            loading={save.isPending || undefined}
            onClick={() => save.mutate()}
          >
            <i className="pi pi-check" />
          </Button>
          <Inplace.Close
            className="pi pi-times cursor-pointer rounded-md p-2 text-app-muted"
            aria-label={t("common.cancel")}
          />
        </div>
        <TimeMutationMessage error={save.error} />
      </Inplace.Content>
    </Inplace.Root>
  );
}
function AssignmentMatrix({ initialDate }: { initialDate: string }) {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<AssignmentMatrixView>("week");
  const [anchor, setAnchor] = useState(initialDate);
  useEffect(() => setAnchor(initialDate), [initialDate]);
  const range = assignmentRange(anchor, view);
  const matrix = useQuery({
    queryKey: ["time-assignment-matrix", range.from, range.to],
    queryFn: () =>
      apiGet<{ data: AssignmentMatrixData }>("/time/assignment-matrix", {
        query: range,
      }).then((response) => response.data),
  });
  const projects = useQuery({
    queryKey: ["eligible-projects"],
    queryFn: () =>
      apiGet<{ data: ProjectChoice[] }>("/time/eligible-projects").then(
        (response) => response.data,
      ),
  });
  const rows = useMemo(() => {
    const byId = new Map<string, ProjectChoice>();
    for (const project of projects.data ?? []) byId.set(project.id, project);
    for (const allocation of matrix.data?.allocations ?? []) {
      byId.set(allocation.project.id, allocation.project);
    }
    return [...byId.values()].sort((left, right) =>
      left.number.localeCompare(right.number),
    );
  }, [matrix.data?.allocations, projects.data]);
  const minutesByCell = useMemo(() => {
    const values = new Map<string, number>();
    for (const allocation of matrix.data?.allocations ?? []) {
      const key = `${allocation.project.id}:${allocation.date}`;
      values.set(key, (values.get(key) ?? 0) + allocation.minutes);
    }
    return values;
  }, [matrix.data?.allocations]);
  const viewOptions = [
    { value: "day", label: t("time.viewDay") },
    { value: "week", label: t("time.viewWeek") },
    { value: "month", label: t("time.viewMonth") },
  ];
  return (
    <div className="mb-5 rounded-2xl bg-app-surface shadow-sm ring-1 ring-app-border">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-app-border p-4">
        <div>
          <h2 className="text-lg font-semibold text-app-text">
            {t("time.assignmentMatrix")}
          </h2>
          <p className="text-sm text-app-muted">
            {t("time.assignmentMatrixDescription")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="small"
            variant="outlined"
            severity="secondary"
            aria-label={t("time.previousPeriod")}
            onClick={() =>
              setAnchor((current) => shiftAssignmentAnchor(current, view, -1))
            }
          >
            <i className="pi pi-chevron-left" />
          </Button>
          <DateInput
            value={anchor}
            onChange={(value) => setAnchor(value)}
            className="w-40"
          />
          <Button
            size="small"
            variant="outlined"
            severity="secondary"
            aria-label={t("time.nextPeriod")}
            onClick={() =>
              setAnchor((current) => shiftAssignmentAnchor(current, view, 1))
            }
          >
            <i className="pi pi-chevron-right" />
          </Button>
          <Dropdown.Root
            value={view}
            options={viewOptions}
            optionLabel="label"
            optionValue="value"
            onValueChange={(event: { value: unknown }) =>
              setView(event.value as AssignmentMatrixView)
            }
          >
            <Dropdown.Trigger className="min-w-32">
              <Dropdown.Value />
              <Dropdown.Indicator className="pi pi-chevron-down" />
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Positioner>
                <Dropdown.Popup>
                  <Dropdown.List />
                </Dropdown.Popup>
              </Dropdown.Positioner>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>
      </div>
      {matrix.isLoading || projects.isLoading ? (
        <LoadingPanel />
      ) : matrix.error || projects.error ? (
        <ErrorPanel error={matrix.error ?? projects.error} />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 min-w-56 border-b border-r border-app-border bg-app-surface px-4 py-3 text-left font-semibold">
                  {t("time.project")}
                </th>
                {(matrix.data?.dates ?? []).map((day) => (
                  <th
                    key={day.date}
                    className="min-w-24 border-b border-app-border px-2 py-3 text-center font-medium"
                  >
                    <span className="block text-xs text-app-muted">
                      {new Intl.DateTimeFormat(i18n.resolvedLanguage, {
                        weekday: "short",
                      }).format(new Date(`${day.date}T12:00:00`))}
                    </span>
                    <span>{day.date.slice(5)}</span>
                    {day.absences.map((absence, index) => (
                      <Tag
                        key={`${absence.kind}-${index}`}
                        className="mt-1 max-w-24"
                        severity={
                          absence.kind === "sick" || absence.kind === "accident"
                            ? "danger"
                            : "warn"
                        }
                      >{`${t(`time.kind.${absence.kind}`)} ${duration(absence.minutes)}`}</Tag>
                    ))}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((project) => (
                <tr key={project.id} className="border-b border-app-border">
                  <th className="sticky left-0 z-10 border-r border-app-border bg-app-surface px-4 py-3 text-left">
                    <span className="block font-semibold">
                      {project.number}
                    </span>
                    <span className="block max-w-52 truncate text-xs font-normal text-app-muted">
                      {project.title}
                    </span>
                  </th>
                  {(matrix.data?.dates ?? []).map((day) => (
                    <td key={day.date} className="px-1 py-1">
                      <AllocationCell
                        project={project}
                        day={day}
                        value={
                          minutesByCell.get(`${project.id}:${day.date}`) ?? 0
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-[var(--p-content-hover-background)] font-medium">
                <th className="sticky left-0 z-10 border-r border-app-border bg-[var(--p-content-hover-background)] px-4 py-3 text-left">
                  {t("time.available")}
                </th>
                {(matrix.data?.dates ?? []).map((day) => (
                  <td
                    key={day.date}
                    className="px-3 py-3 text-right tabular-nums"
                  >
                    {duration(day.attendanceMinutes - day.allocatedMinutes)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          {!rows.length ? (
            <p className="p-6 text-center text-sm text-app-muted">
              {t("time.noEligibleProjects")}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function TimeAssignmentsPage() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [week, setWeek] = useState(monday());
  const [entryDialog, setEntryDialog] = useState<TimeEntry | null>();
  const query = useQuery({
    queryKey: ["time-week", week],
    queryFn: () =>
      apiGet<{
        data: TimesheetData;
      }>("/time/week", {
        query: {
          week,
        },
      }).then((response) => response.data),
  });
  const refresh = async () =>
    client.invalidateQueries({
      queryKey: ["time-week", week],
    });
  const sheet = query.data;
  const editable = sheet ? ["draft", "rejected"].includes(sheet.status) : false;
  const dayRows = useMemo(
    () =>
      (sheet?.days ?? []).map((day) => ({
        ...day,
        absenceTotal: Object.values(day.absenceMinutes).reduce(
          (sum, value) => sum + value,
          0,
        ),
        remainingMinutes: day.netAttendanceMinutes - day.allocatedMinutes,
      })),
    [sheet],
  );
  const absenceEntries = (sheet?.entries ?? []).filter(
    (entry) => entry.kind !== "attendance",
  );
  const removeEntry = useMutation({
    mutationFn: (id: string) => apiDelete(`/time/entries/${id}`),
    onSuccess: refresh,
  });
  return (
    <>
      <PageHeader
        title={t("time.assignments")}
        subtitle={t("time.assignmentsDescription")}
      />
      <AssignmentMatrix initialDate={week} />
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-app-surface p-3 shadow-sm ring-1 ring-app-border">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="small"
            variant="outlined"
            severity="secondary"
            aria-label={t("time.workflow.previousWeek")}
            onClick={() => setWeek(shiftWeek(week, -7))}
          >
            ←
          </Button>
          <DateInput
            value={week}
            onChange={(value) => {
              if (value) setWeek(monday(new Date(`${value}T12:00:00`)));
            }}
            className="w-44"
          />
          <Button
            size="small"
            variant="outlined"
            severity="secondary"
            aria-label={t("time.workflow.nextWeek")}
            onClick={() => setWeek(shiftWeek(week, 7))}
          >
            →
          </Button>
          <Button
            size="small"
            variant="outlined"
            severity="secondary"
            onClick={() => setWeek(monday())}
          >
            {t("common.today")}
          </Button>
        </div>
        {sheet ? (
          <Tag
            severity={
              sheet.status === "approved"
                ? "success"
                : sheet.status === "rejected"
                  ? "danger"
                  : sheet.status === "submitted"
                    ? "info"
                    : "warn"
            }
          >
            {t(`time.status.${sheet.status}`)}
          </Tag>
        ) : null}
      </div>
      {query.isLoading ? (
        <LoadingPanel />
      ) : query.error || !sheet ? (
        <ErrorPanel error={query.error} />
      ) : (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric
              label={t("time.planned")}
              value={duration(sheet.totals.plannedMinutes)}
            />
            <Metric
              label={t("time.worked")}
              value={duration(sheet.totals.netAttendanceMinutes)}
            />
            <Metric
              label={t("time.projectTime")}
              value={duration(sheet.totals.allocatedMinutes)}
            />
            <Metric
              label={t("time.workflow.remaining")}
              value={duration(
                sheet.totals.netAttendanceMinutes -
                  sheet.totals.allocatedMinutes,
              )}
            />
          </div>
          {sheet.rejectionReason ? (
            <Message.Root className="mb-5" severity="error">
              <Message.Content>
                <Message.Text>{`${t("time.rejected")}: ${sheet.rejectionReason}`}</Message.Text>
              </Message.Content>
            </Message.Root>
          ) : null}
          <section className="mb-5 rounded-2xl bg-app-surface p-5 shadow-sm ring-1 ring-app-border">
            <h2 className="mb-4 text-lg font-semibold text-app-text">
              {t("time.week")}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-7">
              {dayRows.map((day) => {
                const progressMax = Math.max(
                  day.plannedMinutes,
                  day.netAttendanceMinutes,
                  1,
                );
                const progressValue = Math.max(
                  0,
                  Math.min(day.netAttendanceMinutes, progressMax),
                );
                return (
                  <div
                    key={day.date}
                    className="rounded-lg border border-app-border bg-app-surface p-3"
                  >
                    <p className="text-xs font-semibold uppercase text-app-muted">
                      {new Intl.DateTimeFormat(undefined, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      }).format(new Date(`${day.date}T12:00:00`))}
                    </p>
                    {day.holiday ? (
                      <p className="mt-2 text-xs font-medium text-brand">
                        {day.holiday.name}
                      </p>
                    ) : null}
                    <div
                      className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--p-content-hover-background)]"
                      role="progressbar"
                      aria-label={t("time.workflow.worked")}
                      aria-valuemin={0}
                      aria-valuemax={progressMax}
                      aria-valuenow={progressValue}
                    >
                      <div
                        className="h-full rounded-full bg-brand"
                        style={{
                          width: `${(progressValue / progressMax) * 100}%`,
                        }}
                      />
                    </div>
                    <dl className="mt-3 space-y-1 text-xs">
                      <DayValue
                        label={t("time.planned")}
                        value={duration(day.plannedMinutes)}
                      />
                      <DayValue
                        label={t("time.worked")}
                        value={duration(day.netAttendanceMinutes)}
                      />
                      <DayValue
                        label={t("time.workflow.absence")}
                        value={duration(day.absenceTotal)}
                      />
                      <DayValue
                        label={t("time.projectTime")}
                        value={duration(day.allocatedMinutes)}
                      />
                      <DayValue
                        label={t("time.workflow.remaining")}
                        value={duration(day.remainingMinutes)}
                        emphasis={day.remainingMinutes !== 0}
                      />
                    </dl>
                  </div>
                );
              })}
            </div>
          </section>
          <div className="grid gap-5 xl:grid-cols-2">
            <section className="rounded-2xl bg-app-surface p-5 shadow-sm ring-1 ring-app-border">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-app-text">
                  {t("time.entries")}
                </h2>
                {editable ? (
                  <Button size="small" onClick={() => setEntryDialog(null)}>
                    {t("time.addEntry")}
                  </Button>
                ) : null}
              </div>
              <DataTable.Root data={absenceEntries} dataKey="id" size="small">
                <DataTable.TableContainer>
                  <DataTable.Table>
                    <DataTable.THead>
                      <DataTable.THeadRow>
                        {[
                          t("common.date"),
                          t("time.type"),
                          t("time.minutes"),
                          t("time.workflow.details"),
                          ...(editable ? [t("time.workflow.actions")] : []),
                        ].map((header) => (
                          <DataTable.THeadCell key={header}>
                            <DataTable.THeadTitle>
                              {header}
                            </DataTable.THeadTitle>
                          </DataTable.THeadCell>
                        ))}
                      </DataTable.THeadRow>
                    </DataTable.THead>
                    <DataTable.TBody>
                      {(context: { item: unknown; index: number }) => {
                        const entry = context.item as TimeEntry;
                        return (
                          <DataTable.Row>
                            <DataTable.Cell>{entry.date}</DataTable.Cell>
                            <DataTable.Cell>
                              {t(`time.kind.${entry.kind}`)}
                            </DataTable.Cell>
                            <DataTable.Cell>
                              {duration(entry.minutes)}
                            </DataTable.Cell>
                            <DataTable.Cell>
                              {entry.startAt
                                ? `${localTime(entry.startAt)}–${localTime(entry.endAt)}`
                                : entry.notes || t("time.workflow.noNotes")}
                            </DataTable.Cell>
                            {editable ? (
                              <DataTable.Cell>
                                <div className="flex gap-2">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    severity="secondary"
                                    onClick={() => setEntryDialog(entry)}
                                  >
                                    {t("common.edit")}
                                  </Button>
                                  <ConfirmButton
                                    onConfirm={() =>
                                      removeEntry.mutate(entry.id)
                                    }
                                    confirmMessage={t(
                                      "time.deleteEntryConfirm",
                                    )}
                                    loading={removeEntry.isPending || undefined}
                                  >
                                    {t("common.delete")}
                                  </ConfirmButton>
                                </div>
                              </DataTable.Cell>
                            ) : null}
                          </DataTable.Row>
                        );
                      }}
                    </DataTable.TBody>
                  </DataTable.Table>
                </DataTable.TableContainer>
              </DataTable.Root>
              {absenceEntries.length === 0 ? (
                <p className="py-4 text-center text-sm text-app-muted">
                  {t("time.workflow.noEntries")}
                </p>
              ) : null}
            </section>
          </div>
          <Dialog.Root
            open={entryDialog !== undefined}
            modal
            dismissable={false}
            closeOnEscape={false}
            style={{
              width: "min(42rem, calc(100vw - 2rem))",
            }}
          >
            <Dialog.Portal>
              <Dialog.Backdrop></Dialog.Backdrop>
              <Dialog.Positioner>
                <Dialog.Popup>
                  <Dialog.Header>
                    <Dialog.Title>
                      {entryDialog
                        ? t("time.workflow.editEntry")
                        : t("time.workflow.newEntry")}
                    </Dialog.Title>
                    <Dialog.HeaderActions>
                      <Button
                        type="button"
                        iconOnly
                        variant="text"
                        severity="secondary"
                        aria-label={t("common.close")}
                        onClick={() => setEntryDialog(undefined)}
                      >
                        <i className="pi pi-times" />
                      </Button>
                    </Dialog.HeaderActions>
                  </Dialog.Header>
                  <Dialog.Content>
                    <EntryForm
                      key={entryDialog?.id ?? "new"}
                      week={week}
                      entry={entryDialog ?? null}
                      onDone={async () => {
                        setEntryDialog(undefined);
                        await refresh();
                      }}
                      onCancel={() => setEntryDialog(undefined)}
                    />
                  </Dialog.Content>
                </Dialog.Popup>
              </Dialog.Positioner>
            </Dialog.Portal>
          </Dialog.Root>
        </>
      )}
    </>
  );
}
function EntryForm({
  week,
  entry,
  onDone,
  onCancel,
}: {
  week: string;
  entry: TimeEntry | null;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState(entry?.date ?? week);
  const [kind, setKind] = useState<(typeof KINDS)[number]>(
    (entry?.kind as (typeof KINDS)[number] | undefined) ?? "vacation",
  );
  const [minutes, setMinutes] = useState(entry?.minutes ?? 60);
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        date,
        kind,
        minutes,
        notes: notes || null,
      };
      if (entry) {
        return apiPatch(`/time/entries/${entry.id}`, {
          ...payload,
          version: entry.version,
        });
      }
      return apiPost("/time/entries", payload);
    },
    onSuccess: onDone,
  });
  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Field label={t("common.date")} required>
        <DateInput value={date} onChange={(value) => setDate(value)} />
      </Field>
      <Field label={t("time.type")} required>
        <Dropdown.Root
          value={kind}
          options={KINDS.map((value) => ({
            value,
            label: t(`time.kind.${value}`),
          }))}
          optionLabel="label"
          optionValue="value"
          onValueChange={(event: { value: unknown }) =>
            setKind(event.value as (typeof KINDS)[number])
          }
          className="w-full"
          fluid
        >
          <Dropdown.Trigger>
            <Dropdown.Value />
            <Dropdown.Indicator className="pi pi-chevron-down" />
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Positioner>
              <Dropdown.Popup>
                <Dropdown.List />
              </Dropdown.Popup>
            </Dropdown.Positioner>
          </Dropdown.Portal>
        </Dropdown.Root>
      </Field>
      <Field label={t("time.minutes")} required>
        <InputNumber.Root
          fluid
          min={1}
          max={24 * 60}
          value={minutes}
          onValueChange={(event: { value: number | null }) =>
            setMinutes(event.value ?? 1)
          }
          className="w-full"
        >
          <InputNumber.Input as={Input} />
        </InputNumber.Root>
      </Field>
      <Field label={t("common.notes")} className="sm:col-span-2">
        <Textarea
          value={notes}
          rows={3}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setNotes(event.target.value)
          }
        />
      </Field>
      <TimeMutationMessage error={save.error} />
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button
          type="button"
          variant="outlined"
          severity="secondary"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={save.isPending || undefined}>
          {entry ? t("common.save") : t("common.add")}
        </Button>
      </div>
    </form>
  );
}
